import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SeverityBadge, SEVERITY } from "@/components/SeverityBadge";
import { downloadCsv, exportIncidentsPdf } from "@/lib/export";
import { Plus, X, CheckCircle, MagnifyingGlassPlus, FileCsv, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLOR = {
  open: "#FF9500",
  investigating: "#00F0FF",
  resolved: "#3ECF8E",
};

export default function Incidents() {
  const [incidents, setIncidents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", severity: "high", assignee: "", threat_id: null });
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get("/incidents");
    setIncidents(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Prefill + open the form when escalating from a threat.
  useEffect(() => {
    const t = location.state?.fromThreat;
    if (t) {
      setForm({
        title: `${t.title} — ${t.target}`,
        description: `Escalated from threat ${t.id}. ${t.description}`,
        severity: t.severity || "high",
        assignee: "",
        threat_id: t.id,
      });
      setShowForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const exportCsv = async () => {
    try { await downloadCsv("/export/incidents", "incidents.csv"); toast.success("CSV exported"); }
    catch (e) { toast.error("Export failed"); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/incidents", form);
      setIncidents((p) => [data, ...p]);
      setShowForm(false);
      setForm({ title: "", description: "", severity: "high", assignee: "", threat_id: null });
      toast.success("Incident created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  const update = async (id, patch) => {
    try {
      const { data } = await api.patch(`/incidents/${id}`, patch);
      setIncidents((p) => p.map((x) => (x.id === id ? data : x)));
      toast.success("Incident updated");
    } catch (err) {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="incidents-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Response</p>
          <h1 className="font-head text-3xl tracking-tighter font-black mt-1">Incident Management</h1>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="export-incidents-csv" onClick={exportCsv}
            className="flex items-center gap-1.5 border border-[#1F1F33] text-[#A1A1AA] uppercase tracking-wider text-[11px] px-3 py-2.5 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors">
            <FileCsv size={14} /> CSV
          </button>
          <button data-testid="export-incidents-pdf" onClick={() => exportIncidentsPdf(incidents)}
            className="flex items-center gap-1.5 border border-[#1F1F33] text-[#A1A1AA] uppercase tracking-wider text-[11px] px-3 py-2.5 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors">
            <FilePdf size={14} /> PDF
          </button>
          <button
            data-testid="new-incident-button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-[#00F0FF] text-black font-bold uppercase tracking-[0.15em] text-[11px] px-4 py-2.5 rounded-none hover:bg-white active:scale-[0.98] transition-all duration-150"
          >
            <Plus size={14} weight="bold" /> New Incident
          </button>
        </div>
      </div>

      <div className="grid gap-4">
        {incidents.map((inc) => (
          <div
            key={inc.id}
            data-testid={`incident-card-${inc.id}`}
            className="bg-[#0C0C14] border border-[#1F1F33] rounded-none p-5 hover:border-[#33334D] transition-colors"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <SeverityBadge level={inc.severity} />
                  <span
                    className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border"
                    style={{ color: STATUS_COLOR[inc.status], borderColor: `${STATUS_COLOR[inc.status]}55` }}
                    data-testid={`incident-status-${inc.id}`}
                  >
                    {inc.status}
                  </span>
                </div>
                <h3 className="font-head text-lg tracking-tight font-bold">{inc.title}</h3>
                <p className="text-xs text-[#71717A] mt-1">{inc.description}</p>
                <div className="text-[11px] text-[#71717A] mt-2">
                  Assignee: <span className="text-[#A1A1AA]">{inc.assignee}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 shrink-0">
                {inc.status !== "investigating" && inc.status !== "resolved" && (
                  <button
                    data-testid={`investigate-${inc.id}`}
                    onClick={() => update(inc.id, { status: "investigating" })}
                    className="flex items-center gap-2 text-[11px] uppercase tracking-wider border border-[#00F0FF]/40 text-[#00F0FF] px-3 py-1.5 hover:bg-[#00F0FF]/10 transition-colors"
                  >
                    <MagnifyingGlassPlus size={14} /> Investigate
                  </button>
                )}
                {inc.status !== "resolved" && (
                  <button
                    data-testid={`resolve-${inc.id}`}
                    onClick={() => update(inc.id, { status: "resolved" })}
                    className="flex items-center gap-2 text-[11px] uppercase tracking-wider border border-[#3ECF8E]/40 text-[#3ECF8E] px-3 py-1.5 hover:bg-[#3ECF8E]/10 transition-colors"
                  >
                    <CheckCircle size={14} /> Resolve
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {incidents.length === 0 && (
          <div className="text-center text-xs text-[#71717A] py-16 border border-dashed border-[#1F1F33]">
            No incidents yet.
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="incident-form-modal">
          <form onSubmit={create} className="w-full max-w-md bg-[#0C0C14] border border-[#1F1F33] p-6 fade-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-head text-xl tracking-tight font-bold">Create Incident</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-[#71717A] hover:text-white">
                <X size={20} />
              </button>
            </div>

            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Title</label>
            <input
              data-testid="incident-title-input"
              required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-[#05050A] border border-[#1F1F33] rounded-none px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF] transition-colors mb-4"
            />

            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Description</label>
            <textarea
              data-testid="incident-description-input"
              rows={3} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-[#05050A] border border-[#1F1F33] rounded-none px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF] transition-colors mb-4 resize-none"
            />

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Severity</label>
                <select
                  data-testid="incident-severity-select"
                  value={form.severity}
                  onChange={(e) => setForm({ ...form, severity: e.target.value })}
                  className="w-full bg-[#05050A] border border-[#1F1F33] rounded-none px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF]"
                >
                  {Object.keys(SEVERITY).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Assignee</label>
                <input
                  data-testid="incident-assignee-input"
                  value={form.assignee}
                  onChange={(e) => setForm({ ...form, assignee: e.target.value })}
                  placeholder="Optional"
                  className="w-full bg-[#05050A] border border-[#1F1F33] rounded-none px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF] placeholder:text-[#71717A]"
                />
              </div>
            </div>

            <button
              data-testid="incident-submit-button"
              type="submit" disabled={busy}
              className="w-full bg-[#00F0FF] text-black font-bold uppercase tracking-[0.2em] text-xs py-3 rounded-none hover:bg-white active:scale-[0.98] transition-all duration-150 disabled:opacity-50"
            >
              {busy ? "Creating…" : "Create Incident"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
